import React, { useState, useEffect } from 'react';
import { Plus, Copy, Check, Trash2, X, Search, ArrowUpDown, MapPin, Truck, CheckCircle2, Clock, Zap, Edit2, RefreshCw, ShieldAlert, Layers, Upload, Download, Calendar, QrCode } from 'lucide-react';
import ClientCardModal from '../components/ClientCardModal';
import DriverCardModal from '../components/DriverCardModal';
import DriverQrConnectModal from '../components/DriverQrConnectModal';
import StopCardModal from '../components/StopCardModal';
import JobCardModal from '../components/JobCardModal';
import HubCardModal from '../components/HubCardModal';
import CsvImportModal from '../components/CsvImportModal';
import CalendarFilterModal from '../components/CalendarFilterModal';

export default function DatabasePage({
  addresses = [],
  hubs = [],
  clients = [],
  clientDefaultTimeWindows = [],
  clientContacts = [],
  rounds = [],
  trucks = [],
  drivers = [],
  objects = [],
  stops = [],
  unassignedStops = [],
  stopDependencies = [],
  jobs = [],
  distanceMatrix = [],
  distanceMatrixCount = 0,
  matrixProgress = null,
  currentTenantId = 1,
  currentTenantName = 'Loops',
  onRecalculateMatrix,
  onAddHub,
  onUpdateHub,
  onAddClient,
  onUpdateClient,
  onAddClientTimeWindow,
  onUpdateClientTimeWindow,
  onDeleteClientTimeWindow,
  onAddTruck,
  onUpdateTruck,
  onDeleteTruck,
  onAddDriver,
  onUpdateDriver,
  onAddStop,
  onUpdateStop,
  onDeleteStop,
  onAddRound,
  onUpdateRound,
  onDeleteRound,
  onUpdateJob,
  onAddContact,
  onDeleteContact,
  onReloadData
}) {
  const [activeSubTab, setActiveSubTab] = useState('clients');
  const [copiedId, setCopiedId] = useState(null);
  const [qrDriver, setQrDriver] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Dedicated Database Grand Totals State
  const [dbStats, setDbStats] = useState({
    stops: stops.length,
    missions: jobs.length,
    clients: clients.length,
    rounds: rounds.length,
    trucks: trucks.length,
    drivers: drivers.length,
    hubs: hubs.length,
    objects: (objects && objects.length) || 0,
    dependencies: stopDependencies.length,
    matrix: distanceMatrixCount || 0
  });

  // Dedicated Date Filter for Stops and Missions (default to Today)
  const todayIso = new Date().toISOString().slice(0, 10);
  const [dateFilter, setDateFilter] = useState({
    startDate: todayIso,
    endDate: todayIso,
    isAll: false
  });
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);

  const [dbStops, setDbStops] = useState([]);
  const [dbJobs, setDbJobs] = useState([]);
  const [isDbStopsLoading, setIsDbStopsLoading] = useState(false);
  const [isDbJobsLoading, setIsDbJobsLoading] = useState(false);

  const fetchDbStats = async () => {
    try {
      const res = await fetch('/api/database/stats');
      if (res.ok) {
        const data = await res.json();
        setDbStats(data);
      }
    } catch (err) {
      console.error('Error fetching db stats:', err);
    }
  };

  const fetchDbStops = async (filter = dateFilter) => {
    setIsDbStopsLoading(true);
    try {
      const url = filter.isAll
        ? '/api/database/stops?all=true'
        : `/api/database/stops?start_date=${filter.startDate}&end_date=${filter.endDate}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setDbStops(data);
      }
    } catch (err) {
      console.error('Error fetching db stops:', err);
    }
    setIsDbStopsLoading(false);
  };

  const fetchDbJobs = async (filter = dateFilter) => {
    setIsDbJobsLoading(true);
    try {
      const url = filter.isAll
        ? '/api/database/missions?all=true'
        : `/api/database/missions?start_date=${filter.startDate}&end_date=${filter.endDate}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setDbJobs(data);
      }
    } catch (err) {
      console.error('Error fetching db jobs:', err);
    }
    setIsDbJobsLoading(false);
  };

  // Format short readable date
  const formatReadableShortDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const d = new Date(parseInt(parts[0], 10), parseInt(parts[1], 10) - 1, parseInt(parts[2], 10));
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const parseMetadata = (raw) => {
    if (!raw) return {};
    if (typeof raw === 'object') return raw;
    const str = String(raw).trim();
    if (!str) return {};
    try {
      return JSON.parse(str);
    } catch (e) {
      return { raw: str };
    }
  };

  // Search & Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [activePrecisions, setActivePrecisions] = useState({ green: true, orange: true, red: true });
  const [truckMatrixFilter, setTruckMatrixFilter] = useState('all');
  const [sortField, setSortField] = useState('id');
  const [sortAsc, setSortAsc] = useState(true);

  // Modal states
  const [selectedClientModal, setSelectedClientModal] = useState(null);
  const [selectedStopModal, setSelectedStopModal] = useState(null);
  const [selectedJobModal, setSelectedJobModal] = useState(null);

  const [editingTruck, setEditingTruck] = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [editingHub, setEditingHub] = useState(null);
  const [editingRound, setEditingRound] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  const [formData, setFormData] = useState({});
  const [objectsList, setObjectsList] = useState(objects || []);
  const [editingObject, setEditingObject] = useState(null);

  // Initial load of stats and data
  useEffect(() => {
    fetchDbStats();
  }, [clients.length, rounds.length, trucks.length, drivers.length, hubs.length, objectsList.length, stopDependencies.length, distanceMatrixCount]);

  useEffect(() => {
    if (activeSubTab === 'stops') {
      fetchDbStops(dateFilter);
    } else if (activeSubTab === 'missions') {
      fetchDbJobs(dateFilter);
    }
  }, [activeSubTab, dateFilter]);

  useEffect(() => {
    if (objects && objects.length > 0) setObjectsList(objects);
    else {
      fetch('/api/objects').then(r => r.json()).then(data => { if (Array.isArray(data)) setObjectsList(data); }).catch(() => {});
    }
  }, [objects]);

  const handleSaveObject = async (obj) => {
    try {
      const metaJson = parseMetadata(obj.metadata);
      const payload = { ...obj, metadata: metaJson };
      if (obj.id) {
        const res = await fetch(`/api/objects/${obj.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const updated = await res.json();
          setObjectsList(prev => prev.map(o => o.id === updated.id ? updated : o));
          setEditingObject(null);
          if (onReloadData) onReloadData();
        }
      } else {
        const res = await fetch('/api/objects', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        if (res.ok) {
          const created = await res.json();
          setObjectsList(prev => [...prev, created]);
          setEditingObject(null);
          if (onReloadData) onReloadData();
        }
      }
    } catch (err) {
      console.error('Error saving object:', err);
    }
  };

  const handleDeleteObject = async (id) => {
    if (!window.confirm("Supprimer cet objet logistique ?")) return;
    try {
      const res = await fetch(`/api/objects/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setObjectsList(prev => prev.filter(o => o.id !== id));
        if (onReloadData) onReloadData();
      }
    } catch (err) {
      console.error('Error deleting object:', err);
    }
  };

  // Driver Shifts State & Handlers
  const [driverShiftsList, setDriverShiftsList] = useState([]);
  const [newShiftDate, setNewShiftDate] = useState(new Date().toISOString().slice(0, 10));
  const [newShiftStartTime, setNewShiftStartTime] = useState('07:30');
  const [newShiftEndTime, setNewShiftEndTime] = useState('17:00');
  const [copySourceDate, setCopySourceDate] = useState(new Date().toISOString().slice(0, 10));
  const [copyTargetDate, setCopyTargetDate] = useState('');

  const fetchDriverShifts = async (driverId) => {
    if (!driverId) return;
    try {
      const res = await fetch(`/api/driver-shifts?driver_id=${driverId}`);
      if (res.ok) {
        const data = await res.json();
        setDriverShiftsList(data);
      }
    } catch (err) {
      console.error('Error fetching driver shifts:', err);
    }
  };

  const handleOpenDriverModal = (driver) => {
    setEditingDriver(driver);
    fetchDriverShifts(driver.id);
  };

  const handleCreateDriverShift = async (e) => {
    e.preventDefault();
    if (!editingDriver || !newShiftDate || !newShiftStartTime || !newShiftEndTime) return;
    const start_datetime = `${newShiftDate}T${newShiftStartTime}:00`;
    const end_datetime = `${newShiftDate}T${newShiftEndTime}:00`;
    try {
      const res = await fetch('/api/driver-shifts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          driver_id: editingDriver.id,
          start_datetime,
          end_datetime
        })
      });
      if (res.ok) {
        await fetchDriverShifts(editingDriver.id);
        if (onReloadData) onReloadData();
      }
    } catch (err) {
      console.error('Error adding shift:', err);
    }
  };

  const handleDeleteDriverShift = async (shiftId) => {
    try {
      const res = await fetch(`/api/driver-shifts/${shiftId}`, { method: 'DELETE' });
      if (res.ok && editingDriver) {
        await fetchDriverShifts(editingDriver.id);
        if (onReloadData) onReloadData();
      }
    } catch (err) {
      console.error('Error deleting shift:', err);
    }
  };

  const handleCopyDriverShifts = async (e) => {
    e.preventDefault();
    if (!copySourceDate || !copyTargetDate || !editingDriver) return;
    try {
      const res = await fetch('/api/driver-shifts/copy-day', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          source_date: copySourceDate,
          target_date: copyTargetDate,
          driver_id: editingDriver.id
        })
      });
      if (res.ok) {
        const data = await res.json();
        alert(`${data.count || 0} quart(s) de travail copié(s) du ${copySourceDate} vers le ${copyTargetDate} !`);
        await fetchDriverShifts(editingDriver.id);
        if (onReloadData) onReloadData();
      }
    } catch (err) {
      console.error('Error copying shifts:', err);
    }
  };

  const handleTriggerRecalculate = async () => {
    setIsCalculating(true);
    if (onRecalculateMatrix) {
      await onRecalculateMatrix();
    }
    setIsCalculating(false);
  };

  const copyDriverLink = (id) => {
    navigator.clipboard.writeText(`http://localhost:3000?driverId=${id}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortAsc(!sortAsc);
    else { setSortField(field); setSortAsc(true); }
  };

  const handleClientChangeInNewStop = (clientId) => {
    const selected = clients.find(c => c.id === parseInt(clientId));
    const clientTw = (clientDefaultTimeWindows || []).find(tw => tw.client_id === parseInt(clientId));
    setFormData(prev => ({
      ...prev,
      client_id: clientId,
      default_round_id: selected?.default_round_id ? String(selected.default_round_id) : prev.default_round_id,
      time_window_start: clientTw?.start_time ? clientTw.start_time.slice(0, 5) : prev.time_window_start,
      time_window_end: clientTw?.end_time ? clientTw.end_time.slice(0, 5) : prev.time_window_end
    }));
  };

  const handleOpenAdd = () => {
    if (activeSubTab === 'stops') {
      const defaultDate = dateFilter.isAll ? todayIso : (dateFilter.startDate || todayIso);
      const firstClient = clients[0] || {};
      const clientTw = (clientDefaultTimeWindows || []).find(tw => tw.client_id === firstClient.id);
      setFormData({
        client_id: firstClient.id ? String(firstClient.id) : '',
        stop_number: `STOP-${Math.floor(Math.random() * 9000 + 1000)}`,
        type: 'delivery',
        delivery_date: defaultDate,
        job_id: '',
        default_round_id: firstClient.default_round_id ? String(firstClient.default_round_id) : '',
        weight_kg: 50.0,
        volume_m3: 0.25,
        revenue: 0.0,
        service_time_mins: 10,
        time_window_start: clientTw?.start_time ? clientTw.start_time.slice(0, 5) : '08:00',
        time_window_end: clientTw?.end_time ? clientTw.end_time.slice(0, 5) : '18:00',
        estimated_arrival: '08:30',
        instructions: '',
        driver_notes: '',
        payment_type: 'none',
        amount_paid: 0.0
      });
    } else if (activeSubTab === 'objects') {
      setEditingObject({ name: '', price: 0.00, weight_per_unit: 0.0, volume_per_unit: 0.0 });
      return;
    } else if (activeSubTab === 'hubs') {
      setEditingHub({
        id: null,
        name: '',
        street: '',
        address: '',
        postal_code: '',
        city: '',
        country: 'Suisse',
        latitude: 46.5197,
        longitude: 6.6323,
        geocoding_precision: 'exact'
      });
      return;
    } else {
      setFormData({});
    }
    setShowAddModal(true);
  };

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    const metaJson = parseMetadata(formData.metadata);
    const dataWithMeta = { ...formData, metadata: metaJson };

    if (activeSubTab === 'clients') await onAddClient(dataWithMeta);
    else if (activeSubTab === 'stops') {
      const payload = {
        ...formData,
        client_id: parseInt(formData.client_id) || clients[0]?.id,
        job_id: formData.job_id ? parseInt(formData.job_id) : null,
        default_round_id: formData.default_round_id ? parseInt(formData.default_round_id) : null,
        weight_kg: parseFloat(formData.weight_kg) || 0,
        volume_m3: parseFloat(formData.volume_m3) || 0,
        revenue: parseFloat(formData.revenue) || 0,
        service_time_mins: parseInt(formData.service_time_mins) || 10,
        time_windows: [{ start: formData.time_window_start || '08:00', end: formData.time_window_end || '18:00' }],
        payment_type: formData.payment_type || 'none',
        amount_paid: parseFloat(formData.amount_paid) || 0.0,
        metadata: metaJson
      };
      await onAddStop(payload);
      await fetchDbStops(dateFilter);
      if (onReloadData) await onReloadData();
    }
    else if (activeSubTab === 'trucks') await onAddTruck(dataWithMeta);
    else if (activeSubTab === 'drivers') await onAddDriver(dataWithMeta);
    else if (activeSubTab === 'hubs') await onAddHub(dataWithMeta);
    else if (activeSubTab === 'rounds' && onAddRound) await onAddRound(dataWithMeta);

    await fetchDbStats();
    setShowAddModal(false);
    setFormData({});
  };

  const q = searchQuery.toLowerCase();

  const filteredClients = clients.filter(c => {
    const matchesQuery = (
      (c.name || '').toLowerCase().includes(q) ||
      (c.technical_key || '').toLowerCase().includes(q) ||
      (c.address || '').toLowerCase().includes(q) ||
      (c.city || '').toLowerCase().includes(q) ||
      (c.id || '').toString().includes(q)
    );
    if (!matchesQuery) return false;

    const precision = (c.geocoding_precision || 'exact').toLowerCase();
    const isGreen = precision === 'exact' || precision === 'manual';
    const isOrange = precision === 'approximate';
    const isRed = precision === 'wrong';

    if (isGreen && !activePrecisions.green) return false;
    if (isOrange && !activePrecisions.orange) return false;
    if (isRed && !activePrecisions.red) return false;

    return true;
  }).sort((a, b) => {
    let valA = a[sortField] || '';
    let valB = b[sortField] || '';
    if (typeof valA === 'number' && typeof valB === 'number') return sortAsc ? valA - valB : valB - valA;
    return sortAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
  });

  const jobsSource = dbJobs.length > 0 || isDbJobsLoading || dateFilter.isAll || dateFilter.startDate ? dbJobs : jobs;
  const filteredMissions = jobsSource.filter(j => 
    (j.display_name || '').toLowerCase().includes(q) ||
    (j.status || '').toLowerCase().includes(q) ||
    (j.id || '').toString().includes(q)
  );

  const stopsSource = dbStops.length > 0 || isDbStopsLoading || dateFilter.isAll || dateFilter.startDate ? dbStops : stops;
  const filteredStops = stopsSource.filter(s =>
    (s.client_name || '').toLowerCase().includes(q) ||
    (s.stop_number || '').toLowerCase().includes(q) ||
    (s.instructions || '').toLowerCase().includes(q) ||
    (s.driver_notes || '').toLowerCase().includes(q) ||
    (s.id || '').toString().includes(q)
  );

  const filteredTrucks = trucks.filter(t =>
    (t.name || '').toLowerCase().includes(q) ||
    (t.plate || '').toLowerCase().includes(q) ||
    (t.valhalla_costing || '').toLowerCase().includes(q) ||
    (t.fuel_type || '').toLowerCase().includes(q) ||
    (t.required_license || '').toLowerCase().includes(q)
  );

  const filteredDrivers = drivers.filter(d =>
    (d.first_name || '').toLowerCase().includes(q) ||
    (d.last_name || '').toLowerCase().includes(q) ||
    (d.email || '').toLowerCase().includes(q) ||
    (d.driving_license || '').toLowerCase().includes(q)
  );

  const filteredHubs = hubs.filter(h =>
    (h.name || '').toLowerCase().includes(q) ||
    (h.city || '').toLowerCase().includes(q)
  );

  const filteredRounds = rounds.filter(r =>
    (r.name || '').toLowerCase().includes(q) ||
    (r.code || '').toLowerCase().includes(q)
  );

  const filteredObjects = (objectsList || []).filter(o =>
    (o.name || '').toLowerCase().includes(q) ||
    (o.id || '').toString().includes(q)
  ).sort((a, b) => {
    let valA = a[sortField] ?? '';
    let valB = b[sortField] ?? '';
    if (sortField === 'is_returned') {
      valA = (a.is_returned === true || a.is_returned === 'true' || a.is_returned === 1) ? 1 : 0;
      valB = (b.is_returned === true || b.is_returned === 'true' || b.is_returned === 1) ? 1 : 0;
    }
    if (typeof valA === 'number' && typeof valB === 'number') return sortAsc ? valA - valB : valB - valA;
    return sortAsc ? String(valA).localeCompare(String(valB)) : String(valB).localeCompare(String(valA));
  });

  const filteredDependencies = (stopDependencies || []).filter(dep => {
    if (!q) return true;
    const predStop = stops.find(s => s.id === dep.predecessor_stop_id);
    const succStop = stops.find(s => s.id === dep.successor_stop_id);
    const predClient = clients.find(c => c.id === predStop?.client_id);
    const succClient = clients.find(c => c.id === succStop?.client_id);
    const str = `${dep.id} ${dep.predecessor_stop_id} ${dep.successor_stop_id} ${dep.predecessor_stop_number || predStop?.stop_number || ''} ${dep.successor_stop_number || succStop?.stop_number || ''} ${dep.predecessor_client_name || predClient?.name || ''} ${dep.successor_client_name || succClient?.name || ''} ${dep.dependency_type || ''}`.toLowerCase();
    return str.includes(q);
  });

  const getVisibleCounts = () => {
    switch (activeSubTab) {
      case 'stops':
        return { visible: filteredStops.length, total: stopsSource.length, label: 'stops' };
      case 'missions':
        return { visible: filteredMissions.length, total: jobsSource.length, label: 'missions' };
      case 'clients':
        return { visible: filteredClients.length, total: clients.length, label: 'clients' };
      case 'rounds':
        return { visible: filteredRounds.length, total: rounds.length, label: 'tournées' };
      case 'trucks':
        return { visible: filteredTrucks.length, total: trucks.length, label: 'véhicules' };
      case 'drivers':
        return { visible: filteredDrivers.length, total: drivers.length, label: 'livreurs' };
      case 'hubs':
        return { visible: filteredHubs.length, total: hubs.length, label: 'hubs' };
      case 'objects':
        return { visible: filteredObjects.length, total: objectsList.length, label: 'objets' };
      case 'dependencies':
        return { visible: filteredDependencies.length, total: stopDependencies.length, label: 'dépendances' };
      case 'matrix':
        return { visible: matrixRows.length, total: matrixTotalCount, label: 'lignes de matrice' };
      default:
        return { visible: 0, total: 0, label: 'éléments' };
    }
  };

  const [matrixRows, setMatrixRows] = useState([]);
  const [matrixPage, setMatrixPage] = useState(1);
  const [matrixLimit] = useState(50);
  const [matrixTotalCount, setMatrixTotalCount] = useState(distanceMatrixCount || 0);
  const [matrixTotalPages, setMatrixTotalPages] = useState(1);
  const [isMatrixLoading, setIsMatrixLoading] = useState(false);

  const fetchMatrixPage = async (page = 1, truckFilter = truckMatrixFilter, search = searchQuery) => {
    setIsMatrixLoading(true);
    try {
      const res = await fetch(`/api/distance-matrix?page=${page}&limit=50&truck_id=${truckFilter}&search=${encodeURIComponent(search)}`);
      if (res.ok) {
        const data = await res.json();
        setMatrixRows(data.rows || []);
        setMatrixTotalCount(data.totalCount || 0);
        setMatrixPage(data.page || 1);
        setMatrixTotalPages(data.totalPages || 1);
      }
    } catch (err) {
      console.error('Error fetching matrix page:', err);
    }
    setIsMatrixLoading(false);
  };

  const handleOpenMatrixTab = () => {
    setActiveSubTab('matrix');
    fetchMatrixPage(1, truckMatrixFilter, searchQuery);
  };

  const handleTruckFilterChange = (val) => {
    setTruckMatrixFilter(val);
    if (activeSubTab === 'matrix') {
      fetchMatrixPage(1, val, searchQuery);
    }
  };

  const [newPredId, setNewPredId] = useState('');
  const [newSuccId, setNewSuccId] = useState('');

  const handleCreateDependency = async (e) => {
    if (e) e.preventDefault();
    if (!newPredId || !newSuccId || newPredId === newSuccId) return;
    try {
      const res = await fetch('/api/stop-dependencies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          predecessor_stop_id: parseInt(newPredId),
          successor_stop_id: parseInt(newSuccId),
          dependency_type: 'precedence'
        })
      });
      if (res.ok) {
        setNewPredId('');
        setNewSuccId('');
        await fetchDbStats();
        if (onReloadData) await onReloadData();
      }
    } catch (err) {
      console.error('Error creating dependency:', err);
    }
  };

  const handleDeleteDependency = async (predId, succId) => {
    try {
      const res = await fetch('/api/stop-dependencies', {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          predecessor_stop_id: predId,
          successor_stop_id: succId
        })
      });
      if (res.ok) {
        await fetchDbStats();
        if (onReloadData) await onReloadData();
      }
    } catch (err) {
      console.error('Error deleting dependency:', err);
    }
  };

  return (
    <div className="database-container">
      {/* ROW 1: TABS ON THE LEFT + GLOBAL IMPORT BUTTON ON THE RIGHT */}
      <div className="database-tabs-header">
        <div className="db-sub-left">
          <div className="db-sub-tabs">
            <button className={`db-sub-tab ${activeSubTab === 'stops' ? 'active' : ''}`} onClick={() => setActiveSubTab('stops')}>
              Stops ({dbStats.stops ?? stops.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'missions' ? 'active' : ''}`} onClick={() => setActiveSubTab('missions')}>
              Missions ({dbStats.missions ?? jobs.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'clients' ? 'active' : ''}`} onClick={() => setActiveSubTab('clients')}>
              Clients ({dbStats.clients ?? clients.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'rounds' ? 'active' : ''}`} onClick={() => setActiveSubTab('rounds')}>
              Tournées ({dbStats.rounds ?? rounds.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'trucks' ? 'active' : ''}`} onClick={() => setActiveSubTab('trucks')}>
              Véhicules ({dbStats.trucks ?? trucks.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'drivers' ? 'active' : ''}`} onClick={() => setActiveSubTab('drivers')}>
              Livreurs ({dbStats.drivers ?? drivers.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'hubs' ? 'active' : ''}`} onClick={() => setActiveSubTab('hubs')}>
              Hubs ({dbStats.hubs ?? hubs.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'objects' ? 'active' : ''}`} onClick={() => setActiveSubTab('objects')}>
              Objets ({dbStats.objects ?? objectsList.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'dependencies' ? 'active' : ''}`} onClick={() => setActiveSubTab('dependencies')}>
              Dépendances ({dbStats.dependencies ?? stopDependencies.length})
            </button>
            <button className={`db-sub-tab ${activeSubTab === 'matrix' ? 'active' : ''}`} onClick={handleOpenMatrixTab}>
              Distance Matrix ({dbStats.matrix ?? (distanceMatrixCount || matrixTotalCount || 0)})
            </button>
          </div>
        </div>

        {/* Global CSV Import button */}
        <button
          type="button"
          className="btn-action"
          onClick={() => setShowImportModal(true)}
          style={{
            background: '#38bdf8',
            color: '#0f172a',
            border: 'none',
            padding: '0 14px',
            height: '34px',
            fontSize: '0.8rem',
            fontWeight: '800',
            borderRadius: '6px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            boxShadow: '0 2px 6px rgba(56, 189, 248, 0.25)',
            whiteSpace: 'nowrap'
          }}
          title="Importer des données (CSV)"
        >
          <Upload size={14} />
          <span>Importer (CSV)</span>
        </button>
      </div>

      {/* ROW 2: TAB-SPECIFIC OPTIONS TOOLBAR */}
      <div className="database-toolbar">
        {/* Left: Date selector, search, filters & visible counts */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          {/* Date Filter Button (Stops & Missions Tabs) - both styled in Blue */}
          {(activeSubTab === 'stops' || activeSubTab === 'missions') && (
            <button
              type="button"
              className="btn-action"
              onClick={() => setIsCalendarOpen(true)}
              style={{
                background: dateFilter.isAll ? '#1e293b' : 'rgba(56, 189, 248, 0.15)',
                color: dateFilter.isAll ? '#94a3b8' : '#38bdf8',
                border: `1px solid ${dateFilter.isAll ? '#334155' : '#38bdf8'}`,
                borderRadius: '6px',
                height: '32px',
                padding: '0 12px',
                fontSize: '0.78rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: dateFilter.isAll ? 'none' : '0 0 10px rgba(56, 189, 248, 0.2)',
                transition: 'all 0.15s ease'
              }}
              title="Cliquer pour changer la date ou sélectionner une période"
            >
              <Calendar size={14} />
              <span>
                {dateFilter.isAll
                  ? 'Toutes les dates (Sans filtre)'
                  : dateFilter.startDate === dateFilter.endDate
                    ? (dateFilter.startDate === todayIso ? `Aujourd'hui (${formatReadableShortDate(dateFilter.startDate)})` : formatReadableShortDate(dateFilter.startDate))
                    : `${formatReadableShortDate(dateFilter.startDate)} - ${formatReadableShortDate(dateFilter.endDate)}`}
              </span>
              <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>▼</span>
            </button>
          )}

          {/* Search Input for Current Tab */}
          <div
            style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              width: '240px',
              height: '32px'
            }}
          >
            <Search size={14} style={{ position: 'absolute', left: '10px', color: '#64748b', pointerEvents: 'none' }} />
            {(() => {
              const tabNamesFrench = {
                stops: 'les stops',
                missions: 'les missions',
                clients: 'les clients',
                rounds: 'les tournées',
                trucks: 'les véhicules',
                drivers: 'les livreurs',
                hubs: 'les hubs',
                objects: 'les objets',
                dependencies: 'les dépendances',
                matrix: 'la matrice'
              };
              return (
                <input
                  type="text"
                  placeholder={`Rechercher dans ${tabNamesFrench[activeSubTab] || activeSubTab}...`}
                  value={searchQuery}
                  onChange={e => {
                    setSearchQuery(e.target.value);
                    if (activeSubTab === 'matrix') {
                      fetchMatrixPage(1, truckMatrixFilter, e.target.value);
                    }
                  }}
                  style={{
                    width: '100%',
                    height: '100%',
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    padding: '0 10px 0 32px',
                    color: '#f8fafc',
                    fontSize: '0.8rem',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              );
            })()}
          </div>

          {/* Matrix Truck Filter */}
          {activeSubTab === 'matrix' && (
            <select
              className="search-input"
              style={{ width: '180px', height: '32px', fontSize: '0.78rem', background: '#0f172a', color: '#38bdf8', borderColor: '#334155' }}
              value={truckMatrixFilter}
              onChange={e => handleTruckFilterChange(e.target.value)}
            >
              <option value="all">Tous les véhicules</option>
              {trucks.map(t => (
                <option key={t.id} value={t.id}>{t.name} ({t.plate})</option>
              ))}
            </select>
          )}

          {/* Geocoding Precision Dots Filter (Clients Tab Only) */}
          {activeSubTab === 'clients' && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '6px',
                height: '32px',
                padding: '0 10px'
              }}
            >
              {/* Green Dot */}
              <button
                type="button"
                onClick={() => setActivePrecisions(prev => ({ ...prev, green: !prev.green }))}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none'
                }}
                title={activePrecisions.green ? 'Désactiver les adresses exactes (Vert)' : 'Activer les adresses exactes (Vert)'}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: activePrecisions.green ? '#22c55e' : '#475569',
                    boxShadow: activePrecisions.green ? '0 0 6px rgba(34, 197, 94, 0.6)' : 'none',
                    transition: 'all 0.15s ease',
                    display: 'inline-block'
                  }}
                />
              </button>

              {/* Orange Dot */}
              <button
                type="button"
                onClick={() => setActivePrecisions(prev => ({ ...prev, orange: !prev.orange }))}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none'
                }}
                title={activePrecisions.orange ? 'Désactiver les adresses approximatives (Orange)' : 'Activer les adresses approximatives (Orange)'}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: activePrecisions.orange ? '#f59e0b' : '#475569',
                    boxShadow: activePrecisions.orange ? '0 0 6px rgba(245, 158, 11, 0.6)' : 'none',
                    transition: 'all 0.15s ease',
                    display: 'inline-block'
                  }}
                />
              </button>

              {/* Red Dot */}
              <button
                type="button"
                onClick={() => setActivePrecisions(prev => ({ ...prev, red: !prev.red }))}
                style={{
                  background: 'none',
                  border: 'none',
                  padding: '2px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  outline: 'none'
                }}
                title={activePrecisions.red ? 'Désactiver les adresses en erreur (Rouge)' : 'Activer les adresses en erreur (Rouge)'}
              >
                <span
                  style={{
                    width: '10px',
                    height: '10px',
                    borderRadius: '50%',
                    background: activePrecisions.red ? '#ef4444' : '#475569',
                    boxShadow: activePrecisions.red ? '0 0 6px rgba(239, 68, 68, 0.6)' : 'none',
                    transition: 'all 0.15s ease',
                    display: 'inline-block'
                  }}
                />
              </button>
            </div>
          )}

          {/* Visible Rows Count Badge */}
          {(() => {
            const countInfo = getVisibleCounts();
            return (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '6px',
                  height: '32px',
                  padding: '0 10px',
                  fontSize: '0.78rem',
                  color: '#94a3b8',
                  whiteSpace: 'nowrap'
                }}
                title="Nombre de lignes visibles avec les filtres actuels"
              >
                <Layers size={13} color="#38bdf8" />
                <span>
                  <strong style={{ color: '#38bdf8' }}>{countInfo.visible}</strong>
                  {countInfo.visible !== countInfo.total && <span> / {countInfo.total}</span>}
                  {' '}{countInfo.label} visible{countInfo.visible > 1 ? 's' : ''}
                </span>
              </div>
            );
          })()}
        </div>

        {/* Right: Tab-specific action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {activeSubTab === 'matrix' && (
            <button
              className="simulator-btn"
              style={{ background: '#f59e0b', color: '#0f172a', padding: '0 14px', height: '32px', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', borderRadius: '6px', border: 'none', cursor: 'pointer' }}
              onClick={handleTriggerRecalculate}
              disabled={isCalculating || !!matrixProgress}
            >
              <RefreshCw size={14} className={isCalculating || !!matrixProgress ? 'spin' : ''} />
              <span>{isCalculating || !!matrixProgress ? 'Calcul en cours...' : 'Recalculer Distance Matrix'}</span>
            </button>
          )}

          {activeSubTab === 'stops' && (
            <button
              type="button"
              className="btn-action"
              onClick={() => {
                window.location.href = '/api/stops/export-csv';
              }}
              style={{ background: 'rgba(34, 197, 94, 0.15)', color: '#22c55e', border: '1px solid #22c55e', padding: '0 12px', height: '32px', fontSize: '0.78rem', fontWeight: '800', borderRadius: '6px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px' }}
              title="Exporter tous les stops au format CSV"
            >
              <Download size={14} />
              <span>Exporter Stops (CSV)</span>
            </button>
          )}

          {activeSubTab !== 'matrix' && activeSubTab !== 'dependencies' && (
            <button
              type="button"
              onClick={handleOpenAdd}
              style={{
                background: '#22c55e',
                color: '#0f172a',
                border: 'none',
                borderRadius: '6px',
                height: '32px',
                padding: '0 14px',
                fontSize: '0.8rem',
                fontWeight: '800',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: '0 2px 6px rgba(34, 197, 94, 0.25)'
              }}
            >
              <Plus size={15} strokeWidth={2.5} />
              <span>Nouveau</span>
            </button>
          )}
        </div>
      </div>

      {/* MATRIX RECALCULATE PROGRESS BAR BANNER */}
      {(isCalculating || !!matrixProgress) && (
        <div style={{ background: '#0f172a', border: '1px solid #38bdf8', borderRadius: '8px', padding: '10px 16px', margin: '12px 24px 0 24px', boxShadow: '0 0 12px rgba(56, 189, 248, 0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.8rem', color: '#38bdf8', fontWeight: '700', marginBottom: '6px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <RefreshCw size={14} className="spin" color="#38bdf8" />
              <span>
                Calcul de la Distance Matrix {matrixProgress ? `(${matrixProgress.current} / ${matrixProgress.total} véhicules - ${matrixProgress.truckName})` : 'en cours...'}
              </span>
            </div>
            <span>{matrixProgress ? `${matrixProgress.percent}%` : 'Initialisation...'}</span>
          </div>

          <div style={{ background: '#1e293b', borderRadius: '4px', height: '8px', overflow: 'hidden', border: '1px solid #334155' }}>
            <div
              style={{
                background: 'linear-gradient(90deg, #38bdf8, #22c55e)',
                height: '100%',
                width: `${matrixProgress ? matrixProgress.percent : 10}%`,
                transition: 'width 0.3s ease'
              }}
            ></div>
          </div>
        </div>
      )}

      {/* CONTENT TABLES */}
      <div className="database-content">
        
        {/* 1. STOPS TABLE */}
        {activeSubTab === 'stops' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Réf Stop</th>
                <th>Client</th>
                <th>Mission</th>
                <th>Tournée Dép.</th>
                <th>Seq</th>
                <th>Action</th>
                <th>Poids</th>
                <th>Volume</th>
                <th>Chiffre d'Affaires</th>
                <th>Arrivée Estimée</th>
                <th>Instructions</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredStops.map(s => {
                const job = (jobs.concat(dbJobs)).find(j => j.id === s.job_id);
                const defaultRound = rounds.find(r => r.id === s.default_round_id);

                return (
                  <tr key={s.id} onClick={() => setSelectedStopModal(s)}>
                    <td>#{s.id}</td>
                    <td><code>{s.stop_number}</code></td>
                    <td><strong>{s.client_name}</strong></td>
                    <td>{job ? job.display_name : <span style={{ color: '#ef4444' }}>Bucket (Non assigné)</span>}</td>
                    <td>
                      {defaultRound ? (
                        <span style={{ color: '#22c55e', fontWeight: '700', fontSize: '0.75rem' }}>{defaultRound.name}</span>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>-</span>
                      )}
                    </td>
                    <td><b>#{s.sequence_order}</b></td>
                    <td><span className="status-pill completed">{s.type}</span></td>
                    <td><b>{Math.round(s.weight_kg)} kg</b></td>
                    <td>{s.volume_m3} m³</td>
                    <td><b style={{ color: '#22c55e' }}>{parseFloat(s.revenue || 0).toFixed(2)} CHF</b></td>
                    <td><b style={{ color: '#38bdf8' }}>{s.estimated_arrival ? s.estimated_arrival.slice(0,5) : '08:00'}</b></td>
                    <td><span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{s.instructions || 'Aucune'}</span></td>
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        className="btn-action"
                        style={{ padding: '2px 6px', color: '#ef4444', borderColor: '#ef4444' }}
                        onClick={async () => {
                          if (window.confirm(`Supprimer le stop #${s.id} ?`)) {
                            if (onDeleteStop) await onDeleteStop(s.id);
                            await fetchDbStops(dateFilter);
                            await fetchDbStats();
                          }
                        }}
                      >
                        <Trash2 size={13} />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {filteredStops.length === 0 && (
                <tr>
                  <td colSpan={13} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                    {isDbStopsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#38bdf8' }}>
                        <RefreshCw size={16} className="spin" />
                        <span>Chargement des stops...</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <span>Aucun stop trouvé pour cette période ({dateFilter.isAll ? 'Toutes dates' : `${formatReadableShortDate(dateFilter.startDate)} - ${formatReadableShortDate(dateFilter.endDate)}`}).</span>
                        <button
                          type="button"
                          className="btn-action"
                          onClick={() => setIsCalendarOpen(true)}
                          style={{ background: '#38bdf8', color: '#0f172a', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                        >
                          📅 Changer de date ou ouvrir le calendrier
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* 2. MISSIONS TABLE */}
        {activeSubTab === 'missions' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom de la Mission</th>
                <th>Chauffeur</th>
                <th>Véhicule</th>
                <th>Statut</th>
                <th>Départ Estimé</th>
                <th>Distance Est.</th>
                <th>Durée Est.</th>
                <th>Coût Estimé</th>
              </tr>
            </thead>
            <tbody>
              {filteredMissions.map(j => {
                const driver = drivers.find(d => d.id === j.driver_id);
                const truck = trucks.find(t => t.id === j.truck_id);

                return (
                  <tr key={j.id} onClick={() => setSelectedJobModal(j)}>
                    <td>#{j.id}</td>
                    <td><strong>{j.display_name || `Mission #${j.id}`}</strong></td>
                    <td>{driver ? `${driver.first_name} ${driver.last_name}` : 'Non assigné'}</td>
                    <td>{truck ? `${truck.name} (${truck.plate})` : 'Non assigné'}</td>
                    <td><span className={`status-pill ${j.status}`}>{j.status}</span></td>
                    <td><b style={{ color: '#22c55e' }}>{j.estimated_departure ? j.estimated_departure.slice(0,5) : '07:30'}</b></td>
                    <td><b>{j.estimated_length_km || 0} km</b></td>
                    <td>{Math.round((j.estimated_duration_sec || 0) / 60)} min</td>
                    <td><b style={{ color: '#38bdf8' }}>{parseFloat(j.estimated_cost || 0).toFixed(2)} CHF</b></td>
                  </tr>
                );
              })}
              {filteredMissions.length === 0 && (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                    {isDbJobsLoading ? (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', color: '#f59e0b' }}>
                        <RefreshCw size={16} className="spin" />
                        <span>Chargement des missions...</span>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px' }}>
                        <span>Aucune mission trouvée pour cette période ({dateFilter.isAll ? 'Toutes dates' : `${formatReadableShortDate(dateFilter.startDate)} - ${formatReadableShortDate(dateFilter.endDate)}`}).</span>
                        <button
                          type="button"
                          className="btn-action"
                          onClick={() => setIsCalendarOpen(true)}
                          style={{ background: '#f59e0b', color: '#0f172a', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '0.78rem', fontWeight: '800', cursor: 'pointer' }}
                        >
                          📅 Changer de date ou ouvrir le calendrier
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {/* 3. CLIENTS TABLE */}
        {activeSubTab === 'clients' && (
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')}>ID <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('code')}>Code <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('name')}>Nom Client <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('city')}>Ville / NPA <ArrowUpDown size={12}/></th>
                <th>Tournée Défaut</th>
                <th>Téléphone</th>
              </tr>
            </thead>
            <tbody>
              {filteredClients.map(c => {
                const round = rounds.find(r => r.id === c.default_round_id);
                const precision = (c.geocoding_precision || 'exact').toLowerCase();
                const dotColor = precision === 'approximate' ? '#f59e0b' : precision === 'wrong' ? '#ef4444' : '#22c55e';

                return (
                  <tr key={c.id} onClick={() => setSelectedClientModal(c)}>
                    <td>#{c.id}</td>
                    <td><code>{c.code}</code></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: dotColor,
                            display: 'inline-block',
                            flexShrink: 0
                          }}
                          title={`Précision: ${c.geocoding_precision || 'exact'}`}
                        />
                        <strong>{c.name}</strong>
                      </div>
                    </td>
                    <td>{c.city} ({c.postal_code})</td>
                    <td>
                      {round ? (
                        <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', fontSize: '0.75rem' }}>
                          {round.name}
                        </span>
                      ) : (
                        <span style={{ color: '#64748b', fontSize: '0.75rem' }}>Aucune</span>
                      )}
                    </td>
                    <td>{c.phone || 'N/A'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* 4. TRUCKS TABLE */}
        {activeSubTab === 'trucks' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom Véhicule</th>
                <th>Plaque</th>
                <th>Permis Requis</th>
                <th>Costing Valhalla</th>
                <th>Carburant</th>
                <th>Conso / km</th>
                <th>Dim (h x l x L)</th>
                <th>Poids (t)</th>
                <th>Capacité Poids</th>
                <th>Capacité Vol</th>
                <th>Coût / km</th>
                <th>Émissions CO₂e</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTrucks.map(t => (
                <tr key={t.id} onClick={() => setEditingTruck(t)}>
                  <td>#{t.id}</td>
                  <td>
                    <span style={{ display: 'inline-block', width: '12px', height: '12px', borderRadius: '50%', backgroundColor: t.color || '#38bdf8', marginRight: '8px', verticalAlign: 'middle', boxShadow: '0 0 5px rgba(0,0,0,0.5)' }} />
                    <strong>{t.name}</strong>
                  </td>
                  <td><code>{t.plate}</code></td>
                  <td>
                    <span style={{ background: 'rgba(245, 158, 11, 0.2)', color: '#f59e0b', padding: '2px 8px', borderRadius: '4px', fontWeight: '800', fontSize: '0.75rem', border: '1px solid #f59e0b' }}>
                      Permis {t.required_license || 'B'}
                    </span>
                  </td>
                  <td>
                    <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', fontSize: '0.75rem' }}>
                      {t.valhalla_costing || 'van'}
                    </span>
                  </td>
                  <td><span style={{ textTransform: 'capitalize', color: t.fuel_type === 'electric' ? '#22c55e' : '#f59e0b', fontWeight: '700' }}>{t.fuel_type || 'diesel'}</span></td>
                  <td>{t.fuel_consumption_per_km || 0.12} L/km</td>
                  <td>{t.height_m}m × {t.width_m}m × {t.length_m}m</td>
                  <td><b>{t.weight_t} t</b></td>
                  <td><b style={{ color: '#22c55e' }}>{Math.round(t.max_payload_weight_kg || 1200)} kg</b></td>
                  <td><b style={{ color: '#f59e0b' }}>{t.max_payload_volume_m3 || 14} m³</b></td>
                  <td><b>{t.cost_per_km || 0.95} CHF/km</b></td>
                  <td><b style={{ color: '#10b981' }}>{t.co2e_per_km !== undefined ? t.co2e_per_km : 0.25} kg/km</b></td>
                  <td onClick={e => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: '6px' }}>
                      <button className="btn-action" style={{ padding: '2px 6px' }} onClick={() => setEditingTruck(t)}>
                        <Edit2 size={13} />
                      </button>
                      <button className="btn-action" style={{ padding: '2px 6px', color: '#ef4444', borderColor: '#ef4444' }} onClick={() => onDeleteTruck && onDeleteTruck(t.id)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 5. DRIVERS TABLE */}
        {activeSubTab === 'drivers' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom & Prénom</th>
                <th>Permis de Conduire</th>
                <th>Email</th>
                <th>Téléphone</th>
                <th>Coût / h</th>
                <th>Connexion & Appareils Chauffeur</th>
              </tr>
            </thead>
            <tbody>
              {filteredDrivers.map(d => (
                <tr key={d.id} onClick={() => setEditingDriver(d)}>
                  <td>#{d.id}</td>
                  <td><strong>{d.first_name} {d.last_name}</strong></td>
                  <td>
                    <span style={{ background: 'rgba(34, 197, 94, 0.2)', color: '#22c55e', padding: '2px 8px', borderRadius: '4px', fontWeight: '800', fontSize: '0.75rem', border: '1px solid #22c55e' }}>
                      Permis {d.driving_license || 'B'}
                    </span>
                  </td>
                  <td>{d.email}</td>
                  <td>{d.phone}</td>
                  <td><b style={{ color: '#22c55e' }}>{d.cost_per_hour || 35.00} CHF/h</b></td>
                  <td onClick={e => e.stopPropagation()}>
                    <button
                      className="btn-action"
                      style={{
                        padding: '6px 14px',
                        fontSize: '0.78rem',
                        background: 'linear-gradient(135deg, #0284c7 0%, #38bdf8 100%)',
                        color: '#0f172a',
                        fontWeight: '800',
                        border: 'none',
                        borderRadius: '6px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        boxShadow: '0 2px 8px rgba(56, 189, 248, 0.3)',
                        cursor: 'pointer'
                      }}
                      onClick={() => setQrDriver(d)}
                      title="Afficher le QR code de connexion et gérer les appareils smartphone appairés"
                    >
                      <QrCode size={14} />
                      <span>Connexion</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 6. HUBS TABLE */}
        {activeSubTab === 'hubs' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nom du Hub</th>
                <th>Adresse Rue & Numéro</th>
                <th>NPA / Ville</th>
                <th>Coordonnées GPS</th>
              </tr>
            </thead>
            <tbody>
              {filteredHubs.map(h => (
                <tr key={h.id} onClick={() => setEditingHub(h)}>
                  <td>#{h.id}</td>
                  <td><strong>{h.name}</strong></td>
                  <td>{h.address}</td>
                  <td>{h.postal_code} {h.city}</td>
                  <td><code>{h.latitude}, {h.longitude}</code></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* 7. ROUNDS TABLE */}
        {activeSubTab === 'rounds' && (
          <table className="data-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Code</th>
                <th>Nom de la Tournée</th>
                <th>Couleur</th>
                <th>Chauffeur Défaut</th>
                <th>Véhicule Défaut</th>
                <th>Dépôt Départ</th>
                <th>Dépôt Retour</th>
                <th>Horaires</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRounds.map(r => {
                const defDriver = drivers.find(d => d.id === r.default_driver_id);
                const defTruck = trucks.find(t => t.id === r.default_truck_id);
                const defStartHub = hubs.find(h => h.id === r.default_start_hub_id);
                const defEndHub = hubs.find(h => h.id === r.default_end_hub_id);

                return (
                  <tr key={r.id} onClick={() => setEditingRound(r)} style={{ cursor: 'pointer' }}>
                    <td>#{r.id}</td>
                    <td><code>{r.code}</code></td>
                    <td><strong>{r.name}</strong></td>
                    <td>
                      <span style={{ background: r.color || '#22c55e', color: '#0f172a', padding: '2px 8px', borderRadius: '4px', fontWeight: '800', fontSize: '0.74rem' }}>
                        {r.color || '#22c55e'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.78rem', color: defDriver ? '#38bdf8' : '#64748b' }}>
                        {defDriver ? `${defDriver.first_name} ${defDriver.last_name}` : '--'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.78rem', color: defTruck ? '#ef4444' : '#64748b' }}>
                        {defTruck ? `${defTruck.name} (${defTruck.plate})` : '--'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.78rem', color: defStartHub ? '#ffffff' : '#64748b' }}>
                        {defStartHub ? defStartHub.name : '--'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.78rem', color: defEndHub ? '#ffffff' : '#64748b' }}>
                        {defEndHub ? defEndHub.name : '--'}
                      </span>
                    </td>
                    <td>
                      <span style={{ fontSize: '0.76rem', color: '#94a3b8' }}>
                        {r.default_start_time || '07:30'} - {r.default_end_time || '17:00'}
                      </span>
                    </td>
                    <td onClick={e => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button className="btn-action" style={{ padding: '2px 6px' }} onClick={() => setEditingRound(r)}>
                          <Edit2 size={13} />
                        </button>
                        <button className="btn-action" style={{ padding: '2px 6px', color: '#ef4444', borderColor: '#ef4444' }} onClick={() => onDeleteRound && onDeleteRound(r.id)}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {/* 8. DISTANCE MATRIX TABLE WITH PAGINATION */}
        {activeSubTab === 'matrix' && (
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID Pair</th>
                    <th>Véhicule</th>
                    <th>Source Address</th>
                    <th>Destination Address</th>
                    <th>Temps (Valhalla Truck)</th>
                    <th>Distance (km / m)</th>
                    <th>Dernière Maj</th>
                  </tr>
                </thead>
                <tbody>
                  {isMatrixLoading ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                        Chargement de la page de la Distance Matrix...
                      </td>
                    </tr>
                  ) : matrixRows.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ textAlign: 'center', padding: '30px', color: '#94a3b8' }}>
                        Aucune entrée trouvée dans la Distance Matrix
                      </td>
                    </tr>
                  ) : (
                    matrixRows.map(dm => {
                      const mins = Math.floor(dm.drive_time_seconds / 60);
                      const secs = dm.drive_time_seconds % 60;
                      const km = (dm.drive_distance_meters / 1000).toFixed(2);

                      return (
                        <tr key={dm.id}>
                          <td><code>#{dm.id}</code></td>
                          <td>
                            <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontWeight: '700', fontSize: '0.75rem' }}>
                              {dm.truck_name || `Camion #${dm.truck_id}`}
                            </span>
                          </td>
                          <td>
                            <div>
                              <strong>{dm.source_street}</strong>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{dm.source_city} (ID #{dm.source_address_id})</div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <strong>{dm.destination_street}</strong>
                              <div style={{ fontSize: '0.72rem', color: '#94a3b8' }}>{dm.destination_city} (ID #{dm.destination_address_id})</div>
                            </div>
                          </td>
                          <td>
                            <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontWeight: '700' }}>
                              ⏱️ {mins} min {secs}s
                            </span>
                          </td>
                          <td>
                            <b style={{ color: '#22c55e' }}>{km} km</b> ({dm.drive_distance_meters} m)
                          </td>
                          <td>
                            <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                              {new Date(dm.last_updated).toLocaleString('fr-CH')}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION CONTROLS */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 16px', background: '#1e293b', borderTop: '1px solid #334155', flexShrink: 0 }}>
              <span style={{ fontSize: '0.78rem', color: '#94a3b8' }}>
                Affichage de {matrixRows.length} lignes — Page <strong style={{ color: '#ffffff' }}>{matrixPage}</strong> sur <strong>{matrixTotalPages}</strong> ({matrixTotalCount} entrées au total)
              </span>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <button
                  className="btn-action"
                  style={{ padding: '4px 14px', fontSize: '0.78rem' }}
                  disabled={matrixPage <= 1 || isMatrixLoading}
                  onClick={() => fetchMatrixPage(matrixPage - 1, truckMatrixFilter, searchQuery)}
                >
                  Précédent
                </button>

                <span style={{ fontSize: '0.78rem', color: '#38bdf8', fontWeight: '700', padding: '0 4px' }}>
                  {matrixPage} / {matrixTotalPages}
                </span>

                <button
                  className="btn-action"
                  style={{ padding: '4px 14px', fontSize: '0.78rem' }}
                  disabled={matrixPage >= matrixTotalPages || isMatrixLoading}
                  onClick={() => fetchMatrixPage(matrixPage + 1, truckMatrixFilter, searchQuery)}
                >
                  Suivant
                </button>
              </div>
            </div>
          </div>
        )}

        {/* TAB: DEPENDENCIES (PRECEDENCE) */}
        {activeSubTab === 'dependencies' && (
          <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column', gap: '16px', overflowY: 'auto' }}>
            {/* Quick Creation Bar */}
            <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: '8px', padding: '14px', display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '0.85rem', fontWeight: '700', color: '#38bdf8', whiteSpace: 'nowrap' }}>
                ➕ Ajouter une dépendance (Ordre de passage) :
              </span>
              <select
                value={newPredId}
                onChange={e => setNewPredId(e.target.value)}
                style={{ flex: 1, minWidth: '220px', background: '#0f172a', color: '#fff', border: '1px solid #334155', padding: '6px 10px', borderRadius: '6px', fontSize: '0.8rem' }}
              >
                <option value="">-- Stop Prédécesseur (Passer AVANT) --</option>
                {stops.map(s => {
                  const c = clients.find(cl => cl.id === s.client_id);
                  const cName = s.client_name || c?.name || s.city || 'Client';
                  return (
                    <option key={s.id} value={s.id}>
                      {s.type === 'pickup' ? '📦↗ [RAMASSAGE]' : '📦↘ [LIVRAISON]'} {s.stop_number || `STOP-${s.id}`} - {cName}
                    </option>
                  );
                })}
              </select>

              <span style={{ fontSize: '0.85rem', fontWeight: '800', color: '#f59e0b', whiteSpace: 'nowrap' }}>➔ DOIT PASSER AVANT ➔</span>

              <select
                value={newSuccId}
                onChange={e => setNewSuccId(e.target.value)}
                style={{ flex: 1, minWidth: '220px', background: '#0f172a', color: '#fff', border: '1px solid #334155', padding: '6px 10px', borderRadius: '6px', fontSize: '0.8rem' }}
              >
                <option value="">-- Stop Successeur (Passer APRÈS) --</option>
                {stops.map(s => {
                  const c = clients.find(cl => cl.id === s.client_id);
                  const cName = s.client_name || c?.name || s.city || 'Client';
                  return (
                    <option key={s.id} value={s.id}>
                      {s.type === 'pickup' ? '📦↗ [RAMASSAGE]' : '📦↘ [LIVRAISON]'} {s.stop_number || `STOP-${s.id}`} - {cName}
                    </option>
                  );
                })}
              </select>

              <button
                onClick={handleCreateDependency}
                disabled={!newPredId || !newSuccId || newPredId === newSuccId}
                style={{
                  background: (newPredId && newSuccId && newPredId !== newSuccId) ? '#38bdf8' : '#334155',
                  color: (newPredId && newSuccId && newPredId !== newSuccId) ? '#0f172a' : '#94a3b8',
                  border: 'none',
                  padding: '7px 18px',
                  borderRadius: '6px',
                  fontWeight: '700',
                  fontSize: '0.82rem',
                  cursor: (newPredId && newSuccId && newPredId !== newSuccId) ? 'pointer' : 'not-allowed'
                }}
              >
                + Créer Dépendance
              </button>
            </div>

            {/* Dependencies Table */}
            <div style={{ flex: 1, overflowY: 'auto', background: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Stop Prédécesseur (Avant)</th>
                    <th>Stop Successeur (Après)</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDependencies.map(dep => {
                    const predStop = stops.find(s => s.id === dep.predecessor_stop_id);
                    const succStop = stops.find(s => s.id === dep.successor_stop_id);

                    const predClient = clients.find(c => c.id === predStop?.client_id);
                    const succClient = clients.find(c => c.id === succStop?.client_id);

                    return (
                      <tr key={dep.id || `${dep.predecessor_stop_id}-${dep.successor_stop_id}`} style={{ background: '#1e293b' }}>
                        <td>#{dep.id || '-'}</td>
                        <td>
                          <div style={{ fontWeight: '700', color: '#38bdf8' }}>
                            {dep.predecessor_stop_number || predStop?.stop_number || `STOP-${dep.predecessor_stop_id}`}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {dep.predecessor_client_name || predClient?.name || 'Client'} ({dep.predecessor_city || predStop?.city || '-'})
                          </div>
                        </td>
                        <td>
                          <div style={{ fontWeight: '700', color: '#22c55e' }}>
                            {dep.successor_stop_number || succStop?.stop_number || `STOP-${dep.successor_stop_id}`}
                          </div>
                          <div style={{ fontSize: '0.75rem', color: '#94a3b8' }}>
                            {dep.successor_client_name || succClient?.name || 'Client'} ({dep.successor_city || succStop?.city || '-'})
                          </div>
                        </td>
                        <td>
                          <span style={{ background: 'rgba(56, 189, 248, 0.15)', color: '#38bdf8', padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '700' }}>
                            {dep.dependency_type || 'precedence'}
                          </span>
                        </td>
                        <td style={{ textAlign: 'right' }}>
                          <button
                            onClick={() => handleDeleteDependency(dep.predecessor_stop_id, dep.successor_stop_id)}
                            style={{ background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#ef4444', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer' }}
                            title="Supprimer la dépendance"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {stopDependencies.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ textAlign: 'center', padding: '24px', color: '#64748b', fontStyle: 'italic' }}>
                        Aucune relation de dépendance enregistrée dans la base de données.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 8. OBJECTS / PACKAGING TAB */}
        {activeSubTab === 'objects' && (
          <table className="data-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('id')} style={{ cursor: 'pointer' }}>ID <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('name')} style={{ cursor: 'pointer' }}>Nom de l'Objet <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('is_returned')} style={{ cursor: 'pointer' }}>Rôle / Type <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>Prix Unitaire (CHF) <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('weight_per_unit')} style={{ cursor: 'pointer' }}>Poids / Unité (kg) <ArrowUpDown size={12}/></th>
                <th onClick={() => handleSort('volume_per_unit')} style={{ cursor: 'pointer' }}>Volume / Unité (m³) <ArrowUpDown size={12}/></th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredObjects.map(obj => {
                const isRet = obj.is_returned === true || obj.is_returned === 'true' || obj.is_returned === 1;
                return (
                  <tr key={obj.id} onClick={() => setEditingObject({ ...obj, is_returned: isRet })} style={{ cursor: 'pointer' }}>
                    <td>#{obj.id}</td>
                    <td><strong>{obj.name}</strong></td>
                    <td>
                      <span className={`status-pill ${isRet ? 'completed' : 'pending'}`}>
                        {isRet ? '📦 Emballage / Retour' : '🏷️ Article Commande'}
                      </span>
                    </td>
                    <td><b style={{ color: '#22c55e' }}>{parseFloat(obj.price || 0).toFixed(2)} CHF</b></td>
                    <td><b>{parseFloat(obj.weight_per_unit || 0).toFixed(3)} kg</b></td>
                    <td>{parseFloat(obj.volume_per_unit || 0).toFixed(4)} m³</td>
                    <td onClick={e => e.stopPropagation()} style={{ textAlign: 'right' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end', alignItems: 'center' }}>
                        <button
                          className="btn-action"
                          style={{ padding: '2px 8px', color: '#38bdf8', borderColor: '#334155' }}
                          onClick={() => setEditingObject({ ...obj, is_returned: isRet })}
                          title="Éditer cet objet"
                        >
                          <Edit2 size={13} />
                        </button>
                        <button
                          className="btn-action"
                          style={{ padding: '2px 8px', color: '#ef4444', borderColor: '#ef4444' }}
                          onClick={() => handleDeleteObject(obj.id)}
                          title="Supprimer cet objet"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filteredObjects.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ textAlign: 'center', padding: '36px', color: '#64748b' }}>
                    Aucun objet ou emballage trouvé dans le catalogue.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

      </div>

      {/* EDIT / CREATE MODAL FOR OBJECTS */}
      {editingObject && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: '460px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#f8fafc' }}>
                {editingObject.id ? `Éditer Objet #${editingObject.id}` : 'Nouvel Objet Catalogue'}
              </h3>
              <button onClick={() => setEditingObject(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
            </div>

            <form onSubmit={async (e) => { e.preventDefault(); await handleSaveObject(editingObject); }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom de l'Objet / Type d'emballage:</label>
                <input type="text" className="search-input" required value={editingObject.name || ''} onChange={e => setEditingObject({ ...editingObject, name: e.target.value })} placeholder="ex: Palette Euro (EPAL), Bac 30L..." />
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: '#38bdf8', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={editingObject.is_returned === true || editingObject.is_returned === 'true' || editingObject.is_returned === 1}
                    onChange={e => setEditingObject({ ...editingObject, is_returned: e.target.checked })}
                    style={{ width: '16px', height: '16px', accentColor: '#22c55e' }}
                  />
                  <span><b>Objet Consigné / Emballage de Retour</b> (Palettes, bacs, roll conteneurs...)</span>
                </label>
                <div style={{ fontSize: '0.68rem', color: '#64748b', marginLeft: '24px', marginTop: '2px' }}>
                  Si décoché, l'objet apparaîtra dans l'onglet des articles / modifications de commande.
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: '#22c55e', display: 'block', marginBottom: '4px' }}>Prix Unitaire (CHF):</label>
                <input type="number" step="0.05" className="search-input" required value={editingObject.price !== undefined ? editingObject.price : ''} onChange={e => setEditingObject({ ...editingObject, price: parseFloat(e.target.value) || 0 })} placeholder="0.00" />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'block', marginBottom: '4px' }}>Poids Unitaire (kg):</label>
                  <input type="number" step="0.001" className="search-input" value={editingObject.weight_per_unit !== undefined ? editingObject.weight_per_unit : ''} onChange={e => setEditingObject({ ...editingObject, weight_per_unit: parseFloat(e.target.value) || 0 })} placeholder="ex: 25.000" />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#38bdf8', display: 'block', marginBottom: '4px' }}>Volume Unitaire (m³):</label>
                  <input type="number" step="0.0001" className="search-input" value={editingObject.volume_per_unit !== undefined ? editingObject.volume_per_unit : ''} onChange={e => setEditingObject({ ...editingObject, volume_per_unit: parseFloat(e.target.value) || 0 })} placeholder="ex: 0.1440" />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Métadonnées (JSON):</label>
                <textarea
                  className="search-input"
                  style={{ width: '100%', minHeight: '50px', fontFamily: 'monospace', fontSize: '0.75rem', resize: 'vertical' }}
                  placeholder='{"key": "value"}'
                  value={typeof editingObject.metadata === 'object' ? JSON.stringify(editingObject.metadata, null, 2) : (editingObject.metadata || '')}
                  onChange={e => setEditingObject({ ...editingObject, metadata: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button type="submit" className="btn-action" style={{ background: '#22c55e', color: '#fff', border: 'none' }}>
                  {editingObject.id ? 'Mettre à jour' : 'Créer l\'Objet'}
                </button>
                <button type="button" className="btn-action" onClick={() => setEditingObject(null)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL FOR TRUCKS */}
      {editingTruck && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, color: '#f8fafc' }}>Éditer Véhicule #{editingTruck.id}</h3>
              <button onClick={() => setEditingTruck(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              const metaJson = parseMetadata(editingTruck.metadata);
              await onUpdateTruck(editingTruck.id, { ...editingTruck, metadata: metaJson });
              setEditingTruck(null);
            }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Nom du Véhicule:</label>
                <input type="text" className="search-input" value={editingTruck.name || ''} onChange={e => setEditingTruck({ ...editingTruck, name: e.target.value })} />
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Plaque d'Immatriculation:</label>
                  <input type="text" className="search-input" value={editingTruck.plate || ''} onChange={e => setEditingTruck({ ...editingTruck, plate: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#38bdf8', display: 'block', marginBottom: '4px', fontWeight: '700' }}>Couleur du Véhicule:</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={editingTruck.color || '#38bdf8'}
                      onChange={e => setEditingTruck({ ...editingTruck, color: e.target.value })}
                      style={{
                        width: '38px',
                        height: '36px',
                        padding: '2px',
                        border: '1px solid #334155',
                        borderRadius: '6px',
                        background: '#1e293b',
                        cursor: 'pointer'
                      }}
                      title="Choisir une couleur"
                    />
                    <input
                      type="text"
                      className="search-input"
                      value={editingTruck.color || '#38bdf8'}
                      onChange={e => setEditingTruck({ ...editingTruck, color: e.target.value })}
                      placeholder="#38bdf8"
                      style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }}
                    />
                  </div>
                </div>
              </div>

              {/* Nuances recommandées */}
              <div>
                <label style={{ fontSize: '0.7rem', color: '#64748b', display: 'block', marginBottom: '4px' }}>Nuances recommandées :</label>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {[
                    '#38bdf8', '#22c55e', '#f59e0b', '#ef4444',
                    '#a855f7', '#ec4899', '#06b6d4', '#6366f1',
                    '#10b981', '#f97316', '#e2e8f0', '#64748b'
                  ].map(c => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setEditingTruck({ ...editingTruck, color: c })}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: c,
                        border: (editingTruck.color || '#38bdf8').toLowerCase() === c.toLowerCase() ? '2px solid #ffffff' : '1px solid rgba(255,255,255,0.2)',
                        boxShadow: (editingTruck.color || '#38bdf8').toLowerCase() === c.toLowerCase() ? `0 0 8px ${c}` : 'none',
                        cursor: 'pointer',
                        padding: 0,
                        transition: 'transform 0.1s ease',
                        transform: (editingTruck.color || '#38bdf8').toLowerCase() === c.toLowerCase() ? 'scale(1.15)' : 'scale(1)'
                      }}
                      title={c}
                    />
                  ))}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'block', marginBottom: '4px' }}>Permis Requis:</label>
                  <select className="search-input" value={editingTruck.required_license || 'B'} onChange={e => setEditingTruck({ ...editingTruck, required_license: e.target.value })}>
                    <option value="B">Permis B</option>
                    <option value="BE">Permis BE</option>
                    <option value="C">Permis C (Poids Lourd)</option>
                    <option value="CE">Permis CE</option>
                    <option value="C1">Permis C1</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#38bdf8', display: 'block', marginBottom: '4px' }}>Costing Valhalla:</label>
                  <select className="search-input" value={editingTruck.valhalla_costing || 'van'} onChange={e => setEditingTruck({ ...editingTruck, valhalla_costing: e.target.value })}>
                    <option value="auto">Auto</option>
                    <option value="truck">Truck (Poids Lourd)</option>
                    <option value="van">Van (Fourgon)</option>
                    <option value="bicycle">Bicycle (Vélo Cargo)</option>
                  </select>
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#22c55e', display: 'block', marginBottom: '4px' }}>Type Carburant:</label>
                  <select className="search-input" value={editingTruck.fuel_type || 'diesel'} onChange={e => setEditingTruck({ ...editingTruck, fuel_type: e.target.value })}>
                    <option value="electric">Électrique (electric)</option>
                    <option value="diesel">Diesel (diesel)</option>
                    <option value="unleaded">Sans Plomb (unleaded)</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Conso / km (L/km ou kWh/km):</label>
                  <input type="number" step="0.001" className="search-input" value={editingTruck.fuel_consumption_per_km || 0.12} onChange={e => setEditingTruck({ ...editingTruck, fuel_consumption_per_km: parseFloat(e.target.value) })} />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Coût au km (CHF/km):</label>
                  <input type="number" step="0.05" className="search-input" value={editingTruck.cost_per_km || 0.95} onChange={e => setEditingTruck({ ...editingTruck, cost_per_km: parseFloat(e.target.value) })} />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#10b981', display: 'block', marginBottom: '4px' }}>CO₂e / km (kg/km):</label>
                  <input type="number" step="0.001" className="search-input" value={editingTruck.co2e_per_km !== undefined ? editingTruck.co2e_per_km : 0.25} onChange={e => setEditingTruck({ ...editingTruck, co2e_per_km: parseFloat(e.target.value) })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Hauteur (m):</label>
                  <input type="number" step="0.1" className="search-input" value={editingTruck.height_m || 2.2} onChange={e => setEditingTruck({ ...editingTruck, height_m: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Largeur (m):</label>
                  <input type="number" step="0.1" className="search-input" value={editingTruck.width_m || 2.0} onChange={e => setEditingTruck({ ...editingTruck, width_m: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Longueur (m):</label>
                  <input type="number" step="0.1" className="search-input" value={editingTruck.length_m || 5.8} onChange={e => setEditingTruck({ ...editingTruck, length_m: parseFloat(e.target.value) })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', color: '#94a3b8' }}>Poids (t):</label>
                  <input type="number" step="0.5" className="search-input" value={editingTruck.weight_t || 3.5} onChange={e => setEditingTruck({ ...editingTruck, weight_t: parseFloat(e.target.value) })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#22c55e', display: 'block', marginBottom: '4px' }}>Capacité Max Poids (kg):</label>
                  <input type="number" className="search-input" value={editingTruck.max_payload_weight_kg || 1200} onChange={e => setEditingTruck({ ...editingTruck, max_payload_weight_kg: parseFloat(e.target.value) })} />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#f59e0b', display: 'block', marginBottom: '4px' }}>Capacité Max Volume (m³):</label>
                  <input type="number" step="0.5" className="search-input" value={editingTruck.max_payload_volume_m3 || 14} onChange={e => setEditingTruck({ ...editingTruck, max_payload_volume_m3: parseFloat(e.target.value) })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Autonomie (km):</label>
                  <input type="number" className="search-input" value={editingTruck.autonomy_km || 400} onChange={e => setEditingTruck({ ...editingTruck, autonomy_km: parseInt(e.target.value) })} />
                </div>

                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Statut Véhicule:</label>
                  <select className="search-input" value={editingTruck.status || 'active'} onChange={e => setEditingTruck({ ...editingTruck, status: e.target.value })}>
                    <option value="active">Actif (Active)</option>
                    <option value="maintenance">En Maintenance</option>
                    <option value="inactive">Inactif</option>
                  </select>
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Métadonnées (JSON):</label>
                <textarea
                  className="search-input"
                  style={{ width: '100%', minHeight: '50px', fontFamily: 'monospace', fontSize: '0.75rem', resize: 'vertical' }}
                  placeholder='{"key": "value"}'
                  value={typeof editingTruck.metadata === 'object' ? JSON.stringify(editingTruck.metadata, null, 2) : (editingTruck.metadata || '')}
                  onChange={e => setEditingTruck({ ...editingTruck, metadata: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button type="submit" className="btn-action" style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', flex: 1 }}>Enregistrer</button>
                <button type="button" className="btn-action" onClick={() => setEditingTruck(null)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* EDIT MODAL FOR DRIVERS (WITH 24H WEEK AGENDA & COPY TOOLS) */}
      {editingDriver && (
        <DriverCardModal
          driver={editingDriver}
          onClose={() => setEditingDriver(null)}
          onSaveDriver={async (id, data) => {
            await onUpdateDriver(id, data);
            setEditingDriver(null);
          }}
          onReloadData={onReloadData}
          onOpenQr={(drv) => setQrDriver(drv)}
        />
      )}

      {/* DRIVER QR CONNECT MODAL */}
      {qrDriver && (
        <DriverQrConnectModal
          driver={qrDriver}
          onClose={() => setQrDriver(null)}
          tenantId={currentTenantId}
          tenantName={currentTenantName}
        />
      )}

      {/* EDIT & CREATE MODAL FOR HUBS (WITH FULL ADDRESS EDITING, LAT/LONGS & LEAFLET MAP) */}
      <HubCardModal
        hub={editingHub}
        isOpen={!!editingHub}
        onClose={() => setEditingHub(null)}
        onSaveHub={async (id, data) => {
          if (id) {
            await onUpdateHub(id, data);
          } else {
            await onAddHub(data);
          }
          await fetchDbStats();
          if (onReloadData) await onReloadData();
          setEditingHub(null);
        }}
        onDeleteHub={async (id) => {
          await fetch(`/api/hubs/${id}`, { method: 'DELETE' });
          await fetchDbStats();
          if (onReloadData) await onReloadData();
          setEditingHub(null);
        }}
      />

      {/* EDIT MODAL FOR ROUNDS */}
      {editingRound && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: '480px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '14px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#38bdf8' }}>Éditer Tournée Modèle #{editingRound.id}</h3>
              <button onClick={() => setEditingRound(null)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const metaJson = parseMetadata(editingRound.metadata);
              await onUpdateRound(editingRound.id, { ...editingRound, metadata: metaJson });
              setEditingRound(null);
            }} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Nom :</label>
                  <input type="text" className="search-input" value={editingRound.name || ''} onChange={e => setEditingRound({ ...editingRound, name: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Code :</label>
                  <input type="text" className="search-input" value={editingRound.code || ''} onChange={e => setEditingRound({ ...editingRound, code: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Couleur :</label>
                  <input type="color" className="search-input" style={{ padding: '2px', height: '34px', cursor: 'pointer' }} value={editingRound.color || '#22c55e'} onChange={e => setEditingRound({ ...editingRound, color: e.target.value })} />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#38bdf8' }}>Chauffeur par défaut :</label>
                  <select className="search-input" value={editingRound.default_driver_id || ''} onChange={e => setEditingRound({ ...editingRound, default_driver_id: parseInt(e.target.value) || null })}>
                    <option value="">Aucun chauffeur</option>
                    {drivers.map(d => (
                      <option key={d.id} value={d.id}>{d.first_name} {d.last_name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#ef4444' }}>Véhicule par défaut :</label>
                  <select className="search-input" value={editingRound.default_truck_id || ''} onChange={e => setEditingRound({ ...editingRound, default_truck_id: parseInt(e.target.value) || null })}>
                    <option value="">Aucun véhicule</option>
                    {trucks.map(t => (
                      <option key={t.id} value={t.id}>{t.name} ({t.plate})</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Dépôt Départ :</label>
                  <select className="search-input" value={editingRound.default_start_hub_id || ''} onChange={e => setEditingRound({ ...editingRound, default_start_hub_id: parseInt(e.target.value) || null })}>
                    <option value="">Hub par défaut</option>
                    {hubs.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#94a3b8' }}>Dépôt Retour :</label>
                  <select className="search-input" value={editingRound.default_end_hub_id || ''} onChange={e => setEditingRound({ ...editingRound, default_end_hub_id: parseInt(e.target.value) || null })}>
                    <option value="">Hub par défaut</option>
                    {hubs.map(h => (
                      <option key={h.id} value={h.id}>{h.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#22c55e' }}>Heure Départ :</label>
                  <input type="text" className="search-input" value={editingRound.default_start_time || '07:30'} onChange={e => setEditingRound({ ...editingRound, default_start_time: e.target.value })} />
                </div>
                <div>
                  <label style={{ fontSize: '0.75rem', color: '#f59e0b' }}>Heure Fin :</label>
                  <input type="text" className="search-input" value={editingRound.default_end_time || '17:00'} onChange={e => setEditingRound({ ...editingRound, default_end_time: e.target.value })} />
                </div>
              </div>

              <div>
                <label style={{ fontSize: '0.75rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>Métadonnées (JSON) :</label>
                <textarea
                  className="search-input"
                  style={{ width: '100%', minHeight: '50px', fontFamily: 'monospace', fontSize: '0.75rem', resize: 'vertical' }}
                  placeholder='{"key": "value"}'
                  value={typeof editingRound.metadata === 'object' ? JSON.stringify(editingRound.metadata, null, 2) : (editingRound.metadata || '')}
                  onChange={e => setEditingRound({ ...editingRound, metadata: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button type="submit" className="btn-action" style={{ background: '#38bdf8', color: '#0f172a', border: 'none', fontWeight: '700', flex: 1 }}>Enregistrer</button>
                <button type="button" className="btn-action" onClick={() => setEditingRound(null)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* CLIENT CARD MODAL */}
      <ClientCardModal
        client={selectedClientModal}
        isOpen={!!selectedClientModal}
        onClose={() => setSelectedClientModal(null)}
        onSaveClient={async (id, data) => {
          if (onUpdateClient) await onUpdateClient(id, data);
        }}
        rounds={rounds}
        clientDefaultTimeWindows={clientDefaultTimeWindows}
        onAddClientTimeWindow={onAddClientTimeWindow}
        onUpdateClientTimeWindow={onUpdateClientTimeWindow}
        onDeleteClientTimeWindow={onDeleteClientTimeWindow}
        clientContacts={clientContacts}
        onAddContact={onAddContact}
        onDeleteContact={onDeleteContact}
        onReloadData={onReloadData}
      />

      {/* STOP CARD MODAL */}
      <StopCardModal
        stop={selectedStopModal}
        isOpen={!!selectedStopModal}
        onClose={() => setSelectedStopModal(null)}
        onSaveStop={async (id, data) => {
          await onUpdateStop(id, data);
          await fetchDbStops(dateFilter);
          await fetchDbStats();
          setSelectedStopModal(null);
        }}
        onDeleteStop={async (id) => {
          if (onDeleteStop) {
            await onDeleteStop(id);
          } else {
            await fetch(`/api/stops/${id}`, { method: 'DELETE' });
          }
          await fetchDbStops(dateFilter);
          await fetchDbStats();
          setSelectedStopModal(null);
        }}
        jobs={jobs.concat(dbJobs)}
        clients={clients}
        rounds={rounds}
        stops={dbStops.length > 0 ? dbStops : stops}
        unassignedStops={unassignedStops}
        stopDependencies={stopDependencies}
      />

      {/* CSV IMPORT MODAL */}
      <CsvImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onReloadData={async () => {
          if (onReloadData) await onReloadData();
          await fetchDbStops(dateFilter);
          await fetchDbJobs(dateFilter);
          await fetchDbStats();
        }}
        initialEntity={activeSubTab === 'matrix' ? 'stops' : activeSubTab}
      />

      {/* JOB CARD MODAL */}
      <JobCardModal
        job={selectedJobModal}
        isOpen={!!selectedJobModal}
        onClose={() => setSelectedJobModal(null)}
        onSaveJob={async (id, data) => {
          await onUpdateJob(id, data);
          await fetchDbJobs(dateFilter);
          await fetchDbStats();
          setSelectedJobModal(null);
        }}
        drivers={drivers}
        trucks={trucks}
        hubs={hubs}
        stops={dbStops.length > 0 ? dbStops : stops}
      />

      {/* CALENDAR FILTER MODAL */}
      <CalendarFilterModal
        isOpen={isCalendarOpen}
        onClose={() => setIsCalendarOpen(false)}
        type={activeSubTab === 'missions' ? 'missions' : 'stops'}
        currentFilter={dateFilter}
        onApplyFilter={(newFilter) => {
          setDateFilter(newFilter);
          if (activeSubTab === 'stops') {
            fetchDbStops(newFilter);
          } else if (activeSubTab === 'missions') {
            fetchDbJobs(newFilter);
          }
        }}
      />

      {/* CREATE ENTITY MODAL */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="overlay-card" style={{ width: activeSubTab === 'stops' ? '640px' : '440px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', borderBottom: '1px solid #334155', paddingBottom: '10px' }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#38bdf8', fontWeight: '800' }}>
                {activeSubTab === 'stops' ? 'Nouveau Stop (Création de A à Z)' : `Nouveau ${activeSubTab}`}
              </h3>
              <button onClick={() => setShowAddModal(false)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
            </div>
            <form onSubmit={handleCreateSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {activeSubTab === 'stops' && (
                <>
                  {/* Section 1: Client & Référence */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1.8fr 1.2fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Client Destinataire *
                      </label>
                      <select
                        className="search-input"
                        required
                        value={formData.client_id || ''}
                        onChange={e => handleClientChangeInNewStop(e.target.value)}
                        style={{ width: '100%' }}
                      >
                        <option value="">-- Sélectionner un client --</option>
                        {clients.map(c => (
                          <option key={c.id} value={c.id}>
                            {c.name} {c.city ? `(${c.city})` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Réf. Stop / Commande
                      </label>
                      <input
                        type="text"
                        placeholder="Ex: CMD-1049"
                        className="search-input"
                        value={formData.stop_number || ''}
                        onChange={e => setFormData({ ...formData, stop_number: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Section 2: Date, Type, Tournée modèle & Mission */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Date de Livraison *
                      </label>
                      <input
                        type="date"
                        className="search-input"
                        required
                        value={formData.delivery_date || ''}
                        onChange={e => setFormData({ ...formData, delivery_date: e.target.value })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Type d'opération
                      </label>
                      <select
                        className="search-input"
                        value={formData.type || 'delivery'}
                        onChange={e => setFormData({ ...formData, type: e.target.value })}
                      >
                        <option value="delivery">📦 Livraison</option>
                        <option value="pickup">📦↗ Ramassage / Collecte</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Tournée modèle par défaut
                      </label>
                      <select
                        className="search-input"
                        value={formData.default_round_id || ''}
                        onChange={e => setFormData({ ...formData, default_round_id: e.target.value })}
                      >
                        <option value="">Aucune tournée par défaut</option>
                        {rounds.map(r => (
                          <option key={r.id} value={r.id}>
                            {r.name} ({r.code || `#${r.id}`})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#f59e0b', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Mission du jour assignée
                      </label>
                      <select
                        className="search-input"
                        value={formData.job_id || ''}
                        onChange={e => setFormData({ ...formData, job_id: e.target.value })}
                      >
                        <option value="">Bucket (Non assigné)</option>
                        {jobs.concat(dbJobs).map(j => (
                          <option key={j.id} value={j.id}>
                            {j.display_name || `Mission #${j.id}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Section 3: Poids, Volume, CA & Temps de service */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Poids (kg)
                      </label>
                      <input
                        type="number"
                        step="0.1"
                        className="search-input"
                        value={formData.weight_kg !== undefined ? formData.weight_kg : 50}
                        onChange={e => setFormData({ ...formData, weight_kg: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Volume (m³)
                      </label>
                      <input
                        type="number"
                        step="0.05"
                        className="search-input"
                        value={formData.volume_m3 !== undefined ? formData.volume_m3 : 0.25}
                        onChange={e => setFormData({ ...formData, volume_m3: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#22c55e', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        CA (CHF)
                      </label>
                      <input
                        type="number"
                        step="0.05"
                        className="search-input"
                        value={formData.revenue !== undefined ? formData.revenue : 0}
                        onChange={e => setFormData({ ...formData, revenue: parseFloat(e.target.value) || 0 })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Service (min)
                      </label>
                      <input
                        type="number"
                        step="1"
                        className="search-input"
                        value={formData.service_time_mins !== undefined ? formData.service_time_mins : 10}
                        onChange={e => setFormData({ ...formData, service_time_mins: parseInt(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Section 4: Horaires & Fenêtres de livraison */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Fenêtre Début
                      </label>
                      <input
                        type="time"
                        className="search-input"
                        value={formData.time_window_start || '08:00'}
                        onChange={e => setFormData({ ...formData, time_window_start: e.target.value })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#38bdf8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Fenêtre Fin
                      </label>
                      <input
                        type="time"
                        className="search-input"
                        value={formData.time_window_end || '18:00'}
                        onChange={e => setFormData({ ...formData, time_window_end: e.target.value })}
                      />
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Arrivée estimée
                      </label>
                      <input
                        type="time"
                        className="search-input"
                        value={formData.estimated_arrival || '08:30'}
                        onChange={e => setFormData({ ...formData, estimated_arrival: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Section 5: Encaissement & Paiement */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Mode d'encaissement
                      </label>
                      <select
                        className="search-input"
                        value={formData.payment_type || 'none'}
                        onChange={e => setFormData({ ...formData, payment_type: e.target.value })}
                      >
                        <option value="none">Aucun (Compte client / Pas d'encaissement)</option>
                        <option value="cash">Espèces (Cash)</option>
                        <option value="card">Carte bancaire / Terminal</option>
                        <option value="invoice">Sur Facture</option>
                      </select>
                    </div>

                    <div>
                      <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                        Montant à encaisser (CHF)
                      </label>
                      <input
                        type="number"
                        step="0.05"
                        className="search-input"
                        value={formData.amount_paid !== undefined ? formData.amount_paid : 0}
                        onChange={e => setFormData({ ...formData, amount_paid: parseFloat(e.target.value) || 0 })}
                      />
                    </div>
                  </div>

                  {/* Section 6: Instructions & Notes */}
                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                      Instructions de livraison
                    </label>
                    <textarea
                      rows={2}
                      placeholder="Ex: Livrer à la rampe arrière, appeler à l'interphone..."
                      className="search-input"
                      style={{ height: 'auto', resize: 'vertical' }}
                      value={formData.instructions || ''}
                      onChange={e => setFormData({ ...formData, instructions: e.target.value })}
                    />
                  </div>

                  <div>
                    <label style={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: '700', display: 'block', marginBottom: '4px' }}>
                      Notes chauffeur / Code d'accès
                    </label>
                    <input
                      type="text"
                      placeholder="Ex: Code portail 4821B, clé sous le paillasson..."
                      className="search-input"
                      value={formData.driver_notes || ''}
                      onChange={e => setFormData({ ...formData, driver_notes: e.target.value })}
                    />
                  </div>
                </>
              )}

              {activeSubTab === 'trucks' && (
                <>
                  <input type="text" placeholder="Nom du véhicule..." className="search-input" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  <input type="text" placeholder="Plaque d'immatriculation (ex: VD 490 265)..." className="search-input" required value={formData.plate || ''} onChange={e => setFormData({ ...formData, plate: e.target.value })} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      type="color"
                      value={formData.color || '#38bdf8'}
                      onChange={e => setFormData({ ...formData, color: e.target.value })}
                      style={{
                        width: '38px',
                        height: '36px',
                        padding: '2px',
                        border: '1px solid #334155',
                        borderRadius: '6px',
                        background: '#1e293b',
                        cursor: 'pointer'
                      }}
                      title="Couleur du véhicule"
                    />
                    <input
                      type="text"
                      placeholder="Couleur (ex: #38bdf8)..."
                      className="search-input"
                      value={formData.color || '#38bdf8'}
                      onChange={e => setFormData({ ...formData, color: e.target.value })}
                      style={{ flex: 1, fontFamily: 'monospace', textTransform: 'uppercase' }}
                    />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
                    <select className="search-input" value={formData.required_license || 'B'} onChange={e => setFormData({ ...formData, required_license: e.target.value })}>
                      <option value="B">Permis B</option>
                      <option value="BE">Permis BE</option>
                      <option value="C">Permis C</option>
                      <option value="CE">Permis CE</option>
                      <option value="C1">Permis C1</option>
                    </select>
                    <select className="search-input" value={formData.valhalla_costing || 'van'} onChange={e => setFormData({ ...formData, valhalla_costing: e.target.value })}>
                      <option value="auto">Auto</option>
                      <option value="truck">Truck</option>
                      <option value="van">Van</option>
                      <option value="bicycle">Bicycle</option>
                    </select>
                    <select className="search-input" value={formData.fuel_type || 'diesel'} onChange={e => setFormData({ ...formData, fuel_type: e.target.value })}>
                      <option value="electric">Electric</option>
                      <option value="diesel">Diesel</option>
                      <option value="unleaded">Unleaded</option>
                    </select>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <input type="number" step="0.05" placeholder="Coût / km (CHF)..." className="search-input" value={formData.cost_per_km !== undefined ? formData.cost_per_km : 0.95} onChange={e => setFormData({ ...formData, cost_per_km: parseFloat(e.target.value) })} />
                    <input type="number" step="0.001" placeholder="CO₂e / km (kg)..." className="search-input" value={formData.co2e_per_km !== undefined ? formData.co2e_per_km : 0.25} onChange={e => setFormData({ ...formData, co2e_per_km: parseFloat(e.target.value) })} />
                  </div>
                </>
              )}

              {activeSubTab === 'drivers' && (
                <>
                  <input type="text" placeholder="Prénom..." className="search-input" required value={formData.first_name || ''} onChange={e => setFormData({ ...formData, first_name: e.target.value })} />
                  <input type="text" placeholder="Nom..." className="search-input" required value={formData.last_name || ''} onChange={e => setFormData({ ...formData, last_name: e.target.value })} />
                  <input type="email" placeholder="Email..." className="search-input" required value={formData.email || ''} onChange={e => setFormData({ ...formData, email: e.target.value })} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <select className="search-input" value={formData.driving_license || 'B'} onChange={e => setFormData({ ...formData, driving_license: e.target.value })}>
                      <option value="B">Permis B</option>
                      <option value="BE">Permis BE</option>
                      <option value="C">Permis C</option>
                      <option value="CE">Permis CE</option>
                      <option value="C1">Permis C1</option>
                    </select>
                    <input type="number" step="0.5" placeholder="Coût / h (CHF)..." className="search-input" value={formData.cost_per_hour || 35.00} onChange={e => setFormData({ ...formData, cost_per_hour: parseFloat(e.target.value) })} />
                  </div>
                </>
              )}

              {activeSubTab === 'clients' && (
                <>
                  <input type="text" placeholder="Raison Sociale / Nom Client..." className="search-input" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  <input type="text" placeholder="Clé technique..." className="search-input" value={formData.technical_key || ''} onChange={e => setFormData({ ...formData, technical_key: e.target.value })} />
                  <input type="text" placeholder="Adresse..." className="search-input" required value={formData.address || ''} onChange={e => setFormData({ ...formData, address: e.target.value })} />
                  <input type="text" placeholder="Ville..." className="search-input" required value={formData.city || ''} onChange={e => setFormData({ ...formData, city: e.target.value })} />
                </>
              )}

              {activeSubTab === 'hubs' && (
                <>
                  <input type="text" placeholder="Nom du Hub (ex: Hub Vevey)..." className="search-input" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  <input type="text" placeholder="Rue et numéro..." className="search-input" required value={formData.street || ''} onChange={e => setFormData({ ...formData, street: e.target.value })} />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <input type="text" placeholder="NPA (1800)..." className="search-input" required value={formData.postal_code || ''} onChange={e => setFormData({ ...formData, postal_code: e.target.value })} />
                    <input type="text" placeholder="Ville (Vevey)..." className="search-input" required value={formData.city || ''} onChange={e => setFormData({ ...formData, city: e.target.value })} />
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                    <input type="number" step="0.000001" placeholder="Latitude (ex: 46.52)..." className="search-input" value={formData.latitude || ''} onChange={e => setFormData({ ...formData, latitude: parseFloat(e.target.value) })} />
                    <input type="number" step="0.000001" placeholder="Longitude (ex: 6.63)..." className="search-input" value={formData.longitude || ''} onChange={e => setFormData({ ...formData, longitude: parseFloat(e.target.value) })} />
                  </div>
                </>
              )}

              {activeSubTab === 'rounds' && (
                <>
                  <input type="text" placeholder="Nom de la Tournée (ex: Lausanne - Ouest)..." className="search-input" required value={formData.name || ''} onChange={e => setFormData({ ...formData, name: e.target.value })} />
                  <input type="text" placeholder="Code Technique (ex: LAUS-OUEST)..." className="search-input" required value={formData.code || ''} onChange={e => setFormData({ ...formData, code: e.target.value })} />
                </>
              )}

              <div>
                <label style={{ fontSize: '0.72rem', color: '#94a3b8', display: 'block', marginBottom: '4px' }}>
                  Métadonnées (JSON) :
                </label>
                <textarea
                  className="search-input"
                  rows={2}
                  style={{ height: 'auto', fontFamily: 'monospace', fontSize: '0.75rem', resize: 'vertical' }}
                  placeholder='{"clé": "valeur"}'
                  value={formData.metadata || ''}
                  onChange={e => setFormData({ ...formData, metadata: e.target.value })}
                />
              </div>

              <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                <button type="submit" className="btn-action" style={{ background: '#22c55e', color: '#fff', border: 'none' }}>Créer</button>
                <button type="button" className="btn-action" onClick={() => setShowAddModal(false)}>Annuler</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
